import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import RadixButton from './RadixButton';
import { cnMerge } from '@/utils/cnMerge';

export interface RadixFileUploadProps {
    onFilesSelected: (files: FileList | null) => void;
    multiple?: boolean;
    disabled?: boolean;
    accept?: string;
    buttonText: string;
    helperText?: string;
    className?: string;
    dropzoneClassName?: string;
    inputName?: string;
}

const RadixFileUpload = ({
    onFilesSelected,
    multiple = true,
    disabled = false,
    accept,
    buttonText,
    helperText,
    className,
    dropzoneClassName,
    inputName = 'files',
}: RadixFileUploadProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        onFilesSelected(event.target.files);
        event.target.value = '';
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (disabled) return;
        setIsDragActive(false);
        onFilesSelected(event.dataTransfer.files);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (!disabled) setIsDragActive(true);
    };

    const handleDragLeave = () => {
        setIsDragActive(false);
    };

    return (
        <div className={cnMerge('w-full', className)}>
            <div
                className={cnMerge(
                    'border border-dashed rounded-md px-4 py-6 min-h-[180px] transition-colors',
                    isDragActive ? 'border-primary-500 bg-primary-50' : 'border-border-default',
                    disabled && 'opacity-60 cursor-not-allowed',
                    dropzoneClassName,
                )}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="text-gray-500"
                        >
                            <path
                                d="M7 18C4.79086 18 3 16.2091 3 14C3 11.9456 4.54872 10.253 6.53815 10.0253C7.32355 7.68833 9.52655 6 12.125 6C15.3307 6 17.9492 8.56881 18.0295 11.7556C19.7442 12.1287 21 13.6564 21 15.5C21 17.6046 19.2876 19.317 17.183 19.317H13"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M12 20V12M12 12L9.5 14.5M12 12L14.5 14.5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <p className="m-0 text-sm text-gray-600">{helperText}</p>
                    <RadixButton
                        variant="outline"
                        size="xs"
                        className="!min-w-[120px]"
                        onClick={() => inputRef.current?.click()}
                        disabled={disabled}
                    >
                        {buttonText}
                    </RadixButton>
                    <input
                        ref={inputRef}
                        type="file"
                        name={inputName}
                        accept={accept}
                        multiple={multiple}
                        className="hidden"
                        onChange={handleInputChange}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
};

export default RadixFileUpload;
